import { useApolloClient } from '@apollo/react-hooks';
import { navigate } from '@reach/router';
import gql from 'not-graphql-tag';
import { useContext, useEffect, useState } from 'react';
import {
  AuthTenant,
  SendOtlResponse,
  ValidateOtlResponse,
} from '../../__generated__/pge-types';
import authServiceInstaller from '../../lib/authServiceInstaller';
import routes from '../../routes';
import useFormState, { convertValidationRules } from '../useFormState';
import { BaseValidationContext } from '../useFormState.types';
import usePgeQuery from '../usePgeQuery';
import { validateEmail } from '../../util/form-validation';
import {
  getFromStorage,
  setInStorage,
  removeFromStorage,
} from '../../util/storage-utils';
import { NotificationsContext } from '../../providers/NotificationsProvider';

type Props = {
  successMessageText: string;
  genericErrorText: string;
  accountLockedText: string;
};

type Model = {
  email: string;
};

const INSTALLER_EMAIL_STORAGE_KEY = 'installerEmail';

const sendOTLQuery = gql`
  query sendOTL($params: SendOTLRequest!) {
    sendOTL(params: $params) {
      isSuccess
    }
  }
`;

const validateOTLTokenQuery = gql`
  query validateOTLToken($params: ValidateOTLRequest!) {
    validateOTLToken(params: $params) {
      customToken
      isExpired
      username
    }
  }
`;

const getToken = (querySearchParam: string): string => {
  const x = querySearchParam.split('request=', 2);
  return x.length === 2 ? x[1] : '';
};

export const createOneTimeLoginValidateFunction = () =>
  convertValidationRules<Model>((context: BaseValidationContext<Model>) => {
    return {
      email: validateEmail,
    };
  });

export default function useInstallerQuickLogin({
  successMessageText,
  genericErrorText,
  accountLockedText,
}: Props) {
  const notificationContext = useContext(NotificationsContext);
  const apolloClient = useApolloClient();
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [requiresEmailConfirmation, setRequiresEmailConfirmation] = useState(
    false,
  );
  const [pendingCustomToken, setPendingCustomToken] = useState<string | null>(
    null,
  );

  const token =
    typeof window !== 'undefined' ? getToken(window.location.search) : '';
  const emailFromStorage: string | undefined = getFromStorage(
    INSTALLER_EMAIL_STORAGE_KEY,
  );

  const defaultFormValues: Model = {
    email: emailFromStorage || '',
  };

  const form = useFormState(
    {
      ...defaultFormValues,
    },
    { validate: createOneTimeLoginValidateFunction() },
  );

  const { data: validateData, loading: oneTimeTokenLoading } = usePgeQuery<{
    validateOTLToken: ValidateOtlResponse;
  }>(validateOTLTokenQuery, {
    variables: {
      params: {
        token,
        tenant: AuthTenant.Installer,
      },
    },
    context: {
      installerPortal: true,
    },
    skip: !token,
    errorPolicy: 'all',
  });

  useEffect(() => {
    const maybeSignIn = async () => {
      if (
        token &&
        validateData?.validateOTLToken?.customToken &&
        !validateData?.validateOTLToken?.isExpired
      ) {
        const customToken = validateData.validateOTLToken.customToken;
        setPendingCustomToken(customToken);

        const username = validateData.validateOTLToken.username;
        if (!username) {
          setErrorMessage(genericErrorText);
          return;
        }

        try {
          setIsLoading(true);
          await authServiceInstaller.signInWithCustomToken(
            customToken,
            username.toLowerCase(),
          );
          setInStorage(INSTALLER_EMAIL_STORAGE_KEY, username);
          await navigate(routes.PGE_PLUS_INSTALLER_PORTAL_JOB_LISTING, {
            replace: true,
          });
        } catch (_error) {
          setErrorMessage(genericErrorText);
        } finally {
          setIsLoading(false);
        }
      }

      if (token && validateData?.validateOTLToken?.isExpired) {
        setErrorMessage(genericErrorText);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    maybeSignIn();
  }, [validateData, token]);

  const handleSubmit = async () => {
    setSuccessMessage(false);
    setIsLoading(true);

    // If we already have a valid custom token but need email confirmation, finish sign-in
    if (requiresEmailConfirmation && pendingCustomToken) {
      try {
        await authServiceInstaller.signInWithCustomToken(
          pendingCustomToken,
          form.values.email.toLowerCase(),
        );
        setInStorage(INSTALLER_EMAIL_STORAGE_KEY, form.values.email);
        await navigate(routes.PGE_PLUS_INSTALLER_PORTAL_JOB_LISTING, {
          replace: true,
        });
      } catch (_error) {
        setErrorMessage(genericErrorText);
      }
      setIsLoading(false);
      return;
    }

    const { data: oneTimeLoginData, errors } = await apolloClient.query<{
      sendOTL: SendOtlResponse;
    }>({
      query: sendOTLQuery,
      variables: {
        params: {
          email: form.values.email.toLowerCase(),
          tenant: AuthTenant.Installer,
        },
      },
      context: {
        installerPortal: true,
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    if (errors?.length) {
      if (errors[0].message?.trim() === 'AccountLocked') {
        setErrorMessage(accountLockedText);
      } else {
        setErrorMessage(genericErrorText);
      }
    } else {
      form.reset(defaultFormValues);
      setSuccessMessage(oneTimeLoginData?.sendOTL.isSuccess ?? false);
      setRequiresEmailConfirmation(false);
      setErrorMessage('');
      notificationContext.setState({
        isOpen: true,
        message: successMessageText,
        severity: 'success',
      });
    }
    setIsLoading(false);
  };

  const handleBack = async (): Promise<void> => {
    setSuccessMessage(false);
    setRequiresEmailConfirmation(false);
    setPendingCustomToken(null);
    setErrorMessage('');
  };

  const handleRequestNewLink = () => {
    setRequiresEmailConfirmation(false);
    setPendingCustomToken(null);
    setErrorMessage('');
    removeFromStorage(INSTALLER_EMAIL_STORAGE_KEY);
  };

  return {
    quickform: form,
    quickLoginhandleSubmit: handleSubmit,
    successMessage,
    token,
    oneTimeTokenLoading,
    handleBack,
    tokenStatus:
      !oneTimeTokenLoading && validateData?.validateOTLToken?.isExpired,
    errorMessage,
    setErrorMessage,
    isLoading,
    requiresEmailConfirmation,
    handleRequestNewLink,
  };
}
